import { useState } from 'react';
import { NoteEditor } from './NoteEditor';
import { MemoryBankNoteMetadatum } from './MemoryBankNoteMetadatum';
import { T } from 'gt-react';
import type {
  MemoryBankNoteMetadata,
  MemoryBankSupportedEntityTypes,
} from '@erp/rpc';
import CustomerPill from '@/components/customer/CustomerPill';
import SupplierPill from '@/components/company/SupplierPill';
import { SystemEntity } from '@erp/shared-types';

export interface NoteEditFormProps {
  /**
   * Initial note content to populate the editor
   */
  initialValue?: string;

  /**
   * Metadata to display as context
   */
  metadata?: Partial<MemoryBankNoteMetadata>;

  /**
   * Whether to show the metadata context section
   */
  showMetadata?: boolean;

  /**
   * Placeholder text for the editor
   */
  placeholder?: string;

  /**
   * Height of the editor in pixels
   */
  height?: number;

  /**
   * Whether the form is currently submitting
   */
  isSubmitting?: boolean;

  /**
   * Called when user saves the note
   */
  onSave: (
    content: string,
    filteredMetadata: Partial<MemoryBankNoteMetadata>,
  ) => void | Promise<void>;

  /**
   * Called when user cancels editing
   */
  onCancel: () => void;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Entity type for context display
   */
  entityType?: MemoryBankSupportedEntityTypes;

  /**
   * Entity ID for context display
   */
  entityId?: string;

  /**
   * Whether metadata pills can be removed
   */
  allowMetadataRemoval?: boolean;
}

// Component to render entity pills based on type
function EntityPill({
  entityType,
  entityId,
}: {
  entityType: MemoryBankSupportedEntityTypes;
  entityId: string;
}) {
  switch (entityType as SystemEntity) {
    case SystemEntity.AR_CUSTOMER:
      return <CustomerPill customerId={entityId} />;
    case SystemEntity.AP_SUPPLIER:
      return <SupplierPill supplierId={entityId} />;
    // Add more entity types as needed
    default:
      return null;
  }
}

export function NoteEditForm({
  initialValue,
  metadata,
  showMetadata = true,
  placeholder = '',
  height,
  isSubmitting,
  onSave,
  onCancel,
  className,
  entityType,
  entityId,
  allowMetadataRemoval = true,
}: NoteEditFormProps) {
  const [removedMetadataKeys, setRemovedMetadataKeys] = useState<Set<string>>(
    new Set(),
  );

  // Filter metadata: remove null, undefined, and removed keys
  const filteredMetadata = metadata
    ? Object.fromEntries(
        Object.entries(metadata).filter(
          ([key, value]) =>
            value !== undefined &&
            value !== null &&
            !removedMetadataKeys.has(key),
        ),
      )
    : {};

  const hasMetadata = Object.keys(filteredMetadata).length > 0;
  const hasEntityInfo = entityType && entityId;

  const handleRemoveMetadata = (key: string) => {
    setRemovedMetadataKeys((prev) => new Set(prev).add(key));
  };

  const handleSave = (content: string) => {
    return onSave(content, filteredMetadata);
  };

  return (
    <div className={className}>
      <NoteEditor
        initialValue={initialValue}
        onSave={handleSave}
        onCancel={onCancel}
        isSubmitting={isSubmitting}
        placeholder={placeholder}
        height={height}
      />

      {/* Metadata Context */}
      {showMetadata && (hasMetadata || hasEntityInfo) && (
        <div className="mt-3 p-2.5 rounded-md bg-gray-50 border border-gray-200">
          <div className="text-xs font-medium text-gray-700 mb-1.5">
            <T context="label for the context metadata section when creating a note">
              Note context
            </T>
          </div>
          {hasEntityInfo && (
            <div className="mb-2">
              <div className="text-xs text-muted-foreground mb-1">
                <T context="label indicating which entity the note will be attached to">
                  Adding note to:
                </T>
              </div>
              <EntityPill
                entityType={entityType as MemoryBankSupportedEntityTypes}
                entityId={entityId as string}
              />
            </div>
          )}
          {hasMetadata && (
            <div>
              <div className="text-xs text-muted-foreground mb-1">
                <T context="label above additional metadata context when creating a note">
                  Additional context:
                </T>
              </div>
              <div className="flex flex-wrap gap-1.5 items-center">
                {Object.entries(filteredMetadata).map(([key, value]) => (
                  <MemoryBankNoteMetadatum
                    name={key}
                    value={
                      value as MemoryBankNoteMetadata[keyof MemoryBankNoteMetadata]
                    }
                    key={key}
                    onRemove={
                      allowMetadataRemoval
                        ? () => handleRemoveMetadata(key)
                        : undefined
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
